"use client";

import { useState } from "react";
import type { HomeFaqItem, Locale } from "@/lib/api";

// Exact class names from the captured production CSS module.
const M = "FaqSection-module__g3Ds3a__";
const A = "HomePage-module__TYHVLa__";

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => {
    const entities: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    };
    return entities[char] || char;
  });
}

function escapeAttr(value: string): string {
  return value.replace(/[&"]/g, (char) => (char === "&" ? "&amp;" : "&quot;"));
}

function safeHref(value: string): string {
  const href = value.replace(/&amp;/g, "&").trim();
  if (!href) return "";
  if (href.startsWith("/")) return href;
  try {
    const url = new URL(href);
    return url.protocol === "http:" || url.protocol === "https:" ? url.toString() : "";
  } catch {
    return "";
  }
}

function answerHtml(value: string): string {
  return escapeHtml(value)
    .replace(/&lt;a\b([\s\S]*?)&gt;([\s\S]*?)&lt;\/a&gt;/gi, (_match, attrs: string, label: string) => {
      const hrefMatch = attrs.match(/href\s*=\s*(?:&quot;([\s\S]*?)&quot;|&#39;([\s\S]*?)&#39;|([^&\s]+))/i);
      const href = safeHref(hrefMatch?.[1] || hrefMatch?.[2] || hrefMatch?.[3] || "");
      if (!href) return label;
      return `<a href="${escapeAttr(href)}">${label}</a>`;
    })
    .replace(/\r?\n/g, "<br />");
}

export function FaqAccordion({
  title,
  items,
  idPrefix = "faq-default-en",
  locale = "en",
}: {
  title?: string;
  items: HomeFaqItem[];
  idPrefix?: string;
  locale?: Locale;
}) {
  const [open, setOpen] = useState(-1);
  const completeItems = items.filter(
    (item) => String(item?.q || "").trim() && String(item?.a || "").trim(),
  );
  if (!completeItems.length) return null;

  const heading = title || (locale === "ru" ? "Частые вопросы" : "Frequently asked questions");

  return (
    <div id="home-faq" className={`${A}anchor`} data-home-section="faq">
      <section className={`${M}section`}>
        <div className={`${M}wrap`}>
          <div className={`${M}inner`}>
            <div className={`${M}head`}>
              <h2 className={`${M}h2`}>{heading}</h2>
            </div>
            <div className={`${M}grid`}>
              {completeItems.map((it, i) => {
                const isOpen = open === i;
                const ansId = `${idPrefix}-${i}`;
                return (
                  <div
                    key={`${it.q}-${i}`}
                    className={`${M}item ${isOpen ? `${M}itemOpen` : ""}`}
                  >
                    <button
                      type="button"
                      className={`${M}qBtn`}
                      aria-expanded={isOpen}
                      aria-controls={ansId}
                      onClick={() => setOpen(isOpen ? -1 : i)}
                    >
                      <span className={`${M}qText`}>{it.q}</span>
                      <span className={`${M}iconBox`} aria-hidden="true">
                        <span className={`${M}plus`} />
                      </span>
                    </button>
                    <div className={`${M}answerWrap`} aria-hidden={!isOpen}>
                      <div
                        id={ansId}
                        className={`${M}answerInner`}
                        dangerouslySetInnerHTML={{ __html: answerHtml(it.a) }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
